/**
 * The form somebody fills in before a run.
 *
 * ## ⚠️ Driven by the live spec, drawn by hand
 *
 * The widgets are keyed on VARIABLE NAME — `document`, `parties`, `signing_rule`,
 * `sample_signatures` — not on type. A generic renderer over `InputSpec.type` would produce a
 * JSON textarea for `parties` and a bare `<select>` of wire values like `initials_and_full_on_blocks`
 * for the rule. That is what the builder's run panel does, correctly, because it must serve any
 * workflow. This serves one, in front of a customer.
 *
 * But the spec is still fetched and still authoritative for `required`, `accept` and `max_length` —
 * and ⚠️ an input this app has no widget for is SHOWN AS UNSUPPORTED rather than skipped. A demo
 * that silently drops a newly-added required field would fail at run time with a message about the
 * workflow, and nobody would think to look here.
 */

import { useState } from 'react'
import type { InputSpec } from '../gateway/types.js'
import { SIGNING_RULES } from './report.js'
import { SAMPLES, type Sample } from './samples.js'

export interface Party {
  name: string
  role: string
}

export interface FormValues {
  document: File | null
  parties: Party[]
  signingRule: string
  samples: File[]
}

export const emptyValues: FormValues = {
  document: null,
  parties: [{ name: '', role: '' }],
  signingRule: 'full_on_blocks',
  samples: [],
}

const KNOWN = new Set(['document', 'parties', 'signing_rule', 'sample_signatures'])

/**
 * ⚠️ `{}` would pass the workflow's Draft-7 schema.
 *
 * `start_node.py:58` is `if not value: continue` — an empty object is skipped before the validator
 * is consulted, and `required: true` does not catch it either because the key IS present. The only
 * guard downstream is a code node that raises `no party names were supplied`, wrapped in a sandbox
 * traceback. Refusing it here is the difference between a form error and an unreadable failure
 * three nodes deep.
 */
export function validate(values: FormValues, specs: InputSpec[]): string[] {
  const problems: string[] = []
  const required = (name: string) => specs.find((s) => s.variable === name)?.required ?? false

  if (required('document') && values.document === null) problems.push('Choose a document to check.')

  const named = values.parties.filter((p) => p.name.trim() !== '')
  if (required('parties') && named.length === 0) problems.push('Name at least one party who should have signed.')

  if (required('signing_rule') && values.signingRule === '') problems.push('Choose a signing rule.')

  const maxSamples = specs.find((s) => s.variable === 'sample_signatures')?.max_length
  if (maxSamples !== undefined && values.samples.length > maxSamples) {
    problems.push(`At most ${maxSamples} sample signatures.`)
  }
  return problems
}

/** The wrapped shape the engine demands: a dict at the top level, never a bare array. */
export function partiesPayload(parties: Party[]): { parties: { name: string; role?: string }[] } {
  return {
    parties: parties
      .filter((p) => p.name.trim() !== '')
      .map((p) => (p.role.trim() === '' ? { name: p.name.trim() } : { name: p.name.trim(), role: p.role.trim() })),
  }
}

interface Props {
  specs: InputSpec[]
  values: FormValues
  onChange: (next: FormValues) => void
  onUseSample: (sample: Sample) => void
  disabled: boolean
}

export function SignatureForm({ specs, values, onChange, onUseSample, disabled }: Props) {
  const [dragging, setDragging] = useState(false)
  const spec = (name: string) => specs.find((s) => s.variable === name)
  const set = (patch: Partial<FormValues>) => onChange({ ...values, ...patch })

  const documentSpec = spec('document')
  const samplesSpec = spec('sample_signatures')
  const unsupported = specs.filter((s) => !KNOWN.has(s.variable))

  return (
    <>
      {/* ⚠️ Not silently dropped — see the module note. */}
      {unsupported.length > 0 && (
        <div className="notice notice--warn">
          This workflow declares {unsupported.length} input
          {unsupported.length === 1 ? '' : 's'} this demo has no field for:{' '}
          <strong>{unsupported.map((s) => s.label || s.variable).join(', ')}</strong>. The run will use the
          engine&rsquo;s defaults for {unsupported.length === 1 ? 'it' : 'them'}.
        </div>
      )}

      {SAMPLES.length > 0 && (
        <div className="samples">
          <span className="samples__label">Try one:</span>
          {SAMPLES.map((s) => (
            <button
              key={s.id}
              type="button"
              className="samples__pick"
              disabled={disabled}
              onClick={() => onUseSample(s)}
              title={s.detail}
              data-sample={s.id}
            >
              {s.title}
            </button>
          ))}
        </div>
      )}

      {/* ── the document ──────────────────────────────────────────────────────────────── */}
      <div className="field">
        <label className="field__label" htmlFor="doc">
          {documentSpec?.label ?? 'Document'}
        </label>
        <p className="field__hint">The contract to check. One PDF.</p>
        <div
          className={`drop${dragging ? ' is-over' : ''}${values.document ? ' is-filled' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            const file = e.dataTransfer.files[0]
            if (file && !disabled) set({ document: file })
          }}
        >
          <input
            id="doc"
            type="file"
            className="drop__input"
            disabled={disabled}
            // Straight from the engine's own declaration, so the picker cannot offer what the
            // workflow will refuse.
            accept={documentSpec?.allowed_file_extensions?.join(',') ?? '.pdf'}
            onChange={(e) => set({ document: e.target.files?.[0] ?? null })}
            data-document
          />
          <span className="drop__text">
            {values.document ? values.document.name : 'Drop a PDF here, or choose a file'}
          </span>
          {values.document && (
            <button type="button" className="btn btn--ghost" disabled={disabled} onClick={() => set({ document: null })}>
              Remove
            </button>
          )}
        </div>
      </div>

      {/* ── the parties ───────────────────────────────────────────────────────────────── */}
      <div className="field">
        <span className="field__label">Who should have signed?</span>
        <p className="field__hint">
          Names as they appear in the document. A role is optional and only helps the model read the
          signature block.
        </p>
        {values.parties.map((party, i) => (
          <div className="party" key={i}>
            <input
              className="input"
              placeholder="Full name"
              value={party.name}
              disabled={disabled}
              data-party-name={i}
              onChange={(e) => {
                const next = [...values.parties]
                next[i] = { ...next[i]!, name: e.target.value }
                set({ parties: next })
              }}
            />
            <input
              className="input party__role"
              placeholder="Role (optional)"
              value={party.role}
              disabled={disabled}
              onChange={(e) => {
                const next = [...values.parties]
                next[i] = { ...next[i]!, role: e.target.value }
                set({ parties: next })
              }}
            />
            <button
              type="button"
              className="btn btn--ghost"
              disabled={disabled || values.parties.length === 1}
              title={values.parties.length === 1 ? 'At least one party is needed' : 'Remove'}
              onClick={() => set({ parties: values.parties.filter((_, at) => at !== i) })}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn"
          disabled={disabled}
          onClick={() => set({ parties: [...values.parties, { name: '', role: '' }] })}
          data-add-party
        >
          Add a party
        </button>
      </div>

      {/* ── the signing rule ──────────────────────────────────────────────────────────── */}
      <div className="field">
        <span className="field__label">What was supposed to be signed?</span>
        <p className="field__hint">
          ⚠️ This changes what counts as a problem. Under a blocks-only rule, a page with no
          signature block is not a failure — it is a page that needed nothing.
        </p>
        <div className="rules">
          {SIGNING_RULES.map((rule) => (
            <label
              key={rule.value}
              className={`rule${values.signingRule === rule.value ? ' is-picked' : ''}`}
              data-rule={rule.value}
            >
              <input
                type="radio"
                name="signing_rule"
                value={rule.value}
                checked={values.signingRule === rule.value}
                disabled={disabled}
                onChange={() => set({ signingRule: rule.value })}
              />
              <span className="rule__title">{rule.title}</span>
              <span className="rule__detail">{rule.detail}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ── sample signatures ─────────────────────────────────────────────────────────── */}
      <div className="field">
        <label className="field__label" htmlFor="samples">
          Sample signatures <span className="field__optional">optional</span>
        </label>
        <p className="field__hint">
          Supplying these turns <strong>identity matching</strong> on: the model additionally reports
          how closely each mark resembles the sample. Without them it checks only that the expected
          marks are present.
        </p>
        <input
          id="samples"
          type="file"
          multiple
          className="input"
          disabled={disabled}
          accept={samplesSpec?.allowed_file_types?.includes('image') ? 'image/*' : undefined}
          onChange={(e) => set({ samples: [...(e.target.files ?? [])] })}
          data-samples
        />
        {values.samples.length > 0 && (
          <ul className="thumbs">
            {values.samples.map((file, i) => (
              <li key={`${file.name}-${i}`} className="thumbs__item">
                <img src={URL.createObjectURL(file)} alt="" />
                <span>{file.name}</span>
              </li>
            ))}
          </ul>
        )}
        {samplesSpec?.max_length !== undefined && (
          <p className="field__hint">At most {samplesSpec.max_length} images.</p>
        )}
      </div>
    </>
  )
}
